import { useSettingsModal } from '@/app-shell/settings/hooks/useSettingsModal';
import { useIntegrationsMarketplaceVisibilityGate } from '@/app-shell/settings/integrations/integrations-marketplace-visibility-gate';
import { ProfileSearchSvg } from '@/assets/icons/profile-search-svg';
import { RemoteDeviceSvg } from '@/assets/icons/remote-device-svg';
import { HeidiTogetherBoxesSvg } from '@/assets/image/free-to-pay/heidi-together-boxes-svg';
import {
  bulkSyncCumulativeProcessedAtom,
  bulkSyncExpectedUploadTotalAtom,
  bulkSyncPhaseAtom,
  bulkSyncUploadProgressAtom,
  chronicleDeviceRecordingInfoAtom,
  chronicleIsPausedAtom,
  chronicleLiveOfflineAtom,
  chronicleReconnectedPendingEndAtom,
  chronicleSessionIdAtom,
} from '@/atoms/chronicle';
import { hasIntegrationAtom, integrationCallbacksAtom } from '@/atoms/integrationAtoms';
import {
  activeSessionIdAtom,
  inFlightRecordingSessionIdAtom,
  pendingRecordingActionAtom,
} from '@/atoms/liveTranscriptionAtom';
import {
  offlineCaptureRunningAtom,
  offlineDismissedSyncedBadgeIdsAtom,
  offlineLocalSessionsAtom,
  offlineNoteFailedDismissedSessionIdsAtom,
  offlineNoteFailedSessionIdsAtom,
  offlineSyncQueueAtom,
  useOfflinePendingServerDeleteIds,
} from '@/atoms/offline-recording';
import { offlinePatientLinkAtomFamily } from '@/atoms/offline-recording/patient-link';
import { evidenceSidebarOpenAtom } from '@/atoms/panelAtoms';
import { usePaywallStates } from '@/atoms/paywallAtoms';
import {
  setSidebarOpenTab,
  sidebarOpenTabAtom,
  SidebarTab,
} from '@/atoms/persistedPreferencesAtoms';
import { scheduleDateAtom, sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import {
  sessionListAnchorsMountedAtom,
  sessionListExpandAnchorAvailableAtom,
  sessionListSpotlightStepAtom,
} from '@/atoms/sessionListSpotlightAtoms';
import {
  sessionlessEvidenceAwarenessDevForceShowAtom,
  sessionlessEvidenceAwarenessDevVariantOverrideAtom,
} from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';
import { sidebarPanelContentAtom } from '@/atoms/sidebarPanelAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import {
  DEFAULT_DATE_FORMAT,
  FILENAME_SAFE_DATETIME_FORMAT,
} from '@/components/constants';
import { DatadogDurationVital } from '@/components/constants/datadog-vitals';
import { SessionMergeIcon } from '@/components/icons/session-merge-icon';
import { useVisibleSidebarPanelContent } from '@/components/layout/Navigation/sidebar-panel/hooks/use-visible-sidebar-panel-content';
import { getSidebarPanelOverlayTriggerProps } from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { isShortcutsDisabledAtom } from '@/components/layout/ShortcutsHelper/shortcutsAtom';
import { canCollapseAtom } from '@/components/layout/header/mobile-app-header/mobile-app-header.atoms';
import { isMobileHeaderSheetOpenAtom } from '@/components/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import { deleteSessionDataAtom } from '@/components/modal/delete-session-modal.atoms';
import { OfflineGatedTooltip } from '@/components/offline-recording/offline-gated-tooltip';
import { offlineGatingMessages } from '@/components/offline-recording/offline-gating-messages';
import { useOfflineAwareSyncStatus } from '@/components/offline-recording/sync-status-badge/hooks/useOfflineAwareSyncStatus';
import { SyncStatusBadge } from '@/components/offline-recording/sync-status-badge/sync-status-badge';
import styles from '@/components/tiptap/shared/styles/index.module.scss';
import { useBlockEditorMarkdown } from '@/components/tiptap/vMarkdown/useBlockEditor';
import EhrIcon from '@/components/ui/ehr-icon';
import {
  MobileDrawerHeader,
  MobileFilterRow,
} from '@/components/ui/mobile-filter-drawer';
import { showSendAsEmailModal } from '@/components/ui/more-dropdown-menu/send-as-email/atoms';
import {
  buildSessionStatusOptions,
  sessionStatusChipConfig,
} from '@/components/ui/session-status/session-status-chip';
import {
  compareSessionsByStatus,
  SESSION_STATUS_DISPLAY_ORDER,
} from '@/components/ui/session-status/session-status-order';
import {
  statusChipVariants,
  statusIconColors,
  statusIconVariants,
} from '@/components/ui/session-status/status.variants';
import { useVisibleSessionStatuses } from '@/components/ui/session-status/use-visible-session-statuses';
import { sessionUrgencyMessages } from '@/components/ui/session-urgency/messages';
import { SessionUrgencyFlag } from '@/components/ui/session-urgency/session-urgency-flag';
import { isSessionUrgent } from '@/components/ui/session-urgency/types';
import { TrackedButton } from '@/components/ui/tracked-button';
import { DatadogDurationVitalStopper } from '@/components/utils/datadogDurationVitalStopper';
import { logCopilotAction } from '@/features/copilot/action-log';
import {
  COPILOT_PRECHART_REQUEST_TTL_MS,
  copilotPrechartBatchAtom,
  copilotPrechartRequestAtom,
} from '@/features/copilot/prechart-atoms';
import {
  IS_UPCOMING_SESSIONS_ENABLED,
  useIsAmbientCopilotEnabled,
  useIsCalendarEventTitleEnabled,
  useIsDesktopChronicleEnabled,
  useIsMultiProviderScheduleEnabled,
  useIsNewSessionListEnabled,
  useIsOfflineRecordingEnabled,
  useIsProblemBasedChartingEnabled,
  useIsReleaseRemoteSyncAsyncWebEnabled,
  useIsScheduleFilterOptionsEnabled,
  useIsSessionPageV2Enabled,
  useIsSessionUrgencyEnabled,
} from '@/features/flags';
import {
  GET_EVIDENCE_AWARENESS_POPUP_VARIANT,
  IS_ASSISTANT_VIEW_ENABLED,
  IS_CONSOLIDATED_SESSIONS_LIST_ENABLED,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
  IS_HIDE_EMAIL_FUNCTIONALITY_ENABLED,
  IS_LIVE_TRANSCRIPTION_INDICATOR_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/features/flags/legacy-flags';
import {
  ability,
  ACTIONS,
  hasUpdatedAbilityAtom,
  PERMISSION_SUBJECTS,
} from '@/helpers/casl/ability';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import { filterAndSortSessions } from '@/helpers/sessionSortHelpers';
import { useIsInterruptedRemoteSession } from '@/hooks/desktop/useIsInterruptedRemoteSession';
import { useIsLiveOfflineRemoteSession } from '@/hooks/desktop/useIsLiveOfflineRemoteSession';
import { useIsLiveRunningRemoteSession } from '@/hooks/desktop/useIsLiveRunningRemoteSession';
import { useIsPureOfflineRemoteSession } from '@/hooks/desktop/useIsPureOfflineRemoteSession';
import { useIsRemoteReconnectedSession } from '@/hooks/desktop/useIsRemoteReconnectedSession';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { useQueryIntegrationsMixinGetSchedule } from '@/hooks/integrations/integrations-framework/mixins/useQueryIntegrationsMixinGetSchedule';
import { useEhrLaunchSessionId } from '@/hooks/integrations/useEhrLaunchSessionId';
import { useHasActiveEhrIntegration } from '@/hooks/integrations/useHasActiveEhrIntegration';
import {
  IntegrationLaunchSource,
  useIntegrationEhrInfo,
} from '@/hooks/integrations/useIntegrationEhrInfo';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { useMutateStatePostIntegrationWorkflowSession } from '@/hooks/mutation/integration/direct-backend/useMutatePostIntegrationWorkflowSession';
import { useMutateUpdateSessionEHRAppointmentId } from '@/hooks/mutation/integration/useMutateUpdateSessionEHRAppointmentId';
import { useMutateCreateNewSession } from '@/hooks/mutation/useMutateCreateNewSession';
import { useMutateCreateAndLinkPatientProfile } from '@/hooks/mutation/useMutateCreatePatientProfile';
import { useMutateDeleteEmptySessions } from '@/hooks/mutation/useMutateDeleteEmptySessions';
import { useMutateLinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateLinkSessionsToPatientProfile';
import { useMutateUpdateSyncDocumentById } from '@/hooks/mutation/useMutateUpdateDocumentById';
import { useMutateUpdateSessionStatus } from '@/hooks/mutation/useMutateUpdateSessionStatus';
import { useQueryInterruptedSessions } from '@/hooks/query/desktop/chronicle/useQueryInterruptedSessions';
import { useQueryEHRAppointments } from '@/hooks/query/integrations/useQueryEHRAppointments';
import { useQueryUserEHR } from '@/hooks/query/integrations/useQueryUserEHR';
import { useCombinedOrganizationInfo } from '@/hooks/query/useCombinedOrganizationInfo';
import { useQueryCombinedSessions } from '@/hooks/query/useQueryCombinedSessions';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { usePrefetchSessionById } from '@/hooks/query/useQuerySessionById';
import { useGetPageSize } from '@/hooks/query/useQuerySessionsV2';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useAuth } from '@/hooks/useAuth';
import { useCanViewOthersSessions } from '@/hooks/useCanViewOthersSessions';
import { useCurrentTime } from '@/hooks/useCurrentTime';
import { useDebouncedNewSession } from '@/hooks/useDebouncedNewSession';
import { useFormatSessionTime } from '@/hooks/useFormatSessionTime';
import { useIsDirectSessionSharingAllowed } from '@/hooks/useIsDirectSessionSharingAllowed';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useNewSession, useNewSessionMutationPending } from '@/hooks/useNewSession';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { useSessionCopy } from '@/hooks/useSessionCopy';
import { useSessionDateDisplay } from '@/hooks/useSessionDateDisplay';
import { useSessionListSpotlightActions } from '@/hooks/useSessionListSpotlightTour';
import { useSessionsListHotkeys } from '@/hooks/useSessionsListHotkeys';
import {
  useNewSessionDelegation,
  useShowClinicianSelector,
} from '@/hooks/useShowClinicianSelector';
import { useShowSessionReview } from '@/hooks/useShowSessionReview';
import { useTeamClinicianList } from '@/hooks/useTeamClinicianList';
import { useTidyUpRecordingLock } from '@/hooks/useTidyUpRecordingLock';
import { useTutorial } from '@/hooks/useTutorial';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { trackShortcutUsed } from '@/lib/analytics/scribe/general';
import { trackPatientSelected } from '@/lib/analytics/scribe/patient';
import { trackPaywallViewed } from '@/lib/analytics/scribe/payments';
import { track, type TrackEvent } from '@/lib/analytics/track';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import { hotkeysList } from '@/lists/hotkeys';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { PaywallSource } from '@/models/analytics/features/payments';
import { useSessionsPanelTracking } from '@/models/analytics/features/sessions-panel-tracking';
import {
  trackScheduleSynced,
  trackScheduleUploadCompleted,
  trackScheduleUploadProcessingFailed,
  trackScheduleUploadPromoClicked,
  trackScheduleUploadPromoViewed,
  trackScheduleUploadSessionsCreated,
  trackScheduleViewed,
} from '@/models/analytics/features/trackSessionScheduleAnalytics';
import { useSessionSearchTracking } from '@/models/analytics/features/useSessionSearchTracking';
import { ShortcutContext } from '@/models/analytics/segment-names';
import { isOfflineDeletableStatus } from '@/types/offline-recording';
import { isOnPrem } from '@/utils/buildProfile';
import { enumToDescription } from '@/utils/enumToDescription';
import { getEhrApptTime, TIME_FORMAT } from '@/utils/integration/parse-ehr-appt-time';
import { printDocument } from '@/utils/printHelpers';
import { datadogRum } from '@datadog/browser-rum';
import { useSessionRecordingStore } from '@heidi/capture';
import { useUserTier } from '@heidi/commerce/data';
import { Loader } from '@heidi/design-system/loader';
import { type SubscriptionTier } from '@heidi/schema/ml-scribe';
import { type SessionListHost } from '@heidi/session';
import { exportFontModalAtom } from '@heidi/session/state';
import { getDefaultStore, useAtom, useAtomValue, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { type ComponentType, createElement } from 'react';

import { useSessionListVersion } from './useSessionListVersion';
import { useShowSessionsListAssistantView } from './useShowSessionsListAssistantView';

// Only the list table renders these two, but every route that mounts the sidebar
// imports this host. Static imports would put their patient-linking cone in
// those routes' first load, so they load on first render instead.
export const loadPatientLinkAvatar = () =>
  import('./patient-link-avatar').then((mod) => ({ default: mod.PatientLinkAvatar }));

export const loadSessionListLinkOverlays = () =>
  import('./session-list-link-overlays').then((mod) => ({
    default: mod.SessionListLinkOverlays,
  }));

const rethrowLoadError = ({ error }: { error?: Error | null }) => {
  if (error) throw error;
  return null;
};

// Holds the `sm` avatar's footprint (24px, no shrink) so the patient name does
// not shift right when the avatar arrives.
export const loadConsolidatedSessionLinkAvatar = () =>
  import('./consolidatedSessionLinkAvatar').then((mod) => ({
    default: mod.ConsolidatedSessionLinkAvatar,
  }));

export const loadSessionsListFooterLinkControl = () =>
  import('./sessions-list-footer-link-control').then((mod) => ({
    default: mod.SessionsListFooterLinkControl,
  }));

export const loadAccordionNavV3Button = () =>
  import('@/components/layout/link/accordion-nav-v3-button');

export const loadUploadSessionScheduleModal = () =>
  import('@/components/layout/lazy-session-dialogs').then((mod) => ({
    default: mod.UploadSessionScheduleModal,
  }));

export const loadSessionlessEvidenceAwarenessPopover = () =>
  import('@/components/modal/sessionless-evidence-awareness-popover').then((mod) => ({
    default: (props: { variant: 'sidebar' }) =>
      createElement(
        mod.SessionlessEvidenceAwarenessPopover as ComponentType<{ variant: 'sidebar' }>,
        props
      ),
  }));

export const loadEhrRePairBanner = () =>
  import('@/components/integrations/ehr-re-pair-banner').then((mod) => ({
    default: mod.EhrRePairBanner,
  }));

const PatientLinkAvatarPlaceholder = ({ error }: { error?: Error | null }) => {
  if (error) throw error;
  return createElement('span', {
    'aria-hidden': true,
    className: 'block size-6 shrink-0',
    'data-testid': 'session-list-patient-avatar-placeholder',
  });
};

const PatientLinkAvatar = dynamic(loadPatientLinkAvatar, {
  ssr: false,
  loading: PatientLinkAvatarPlaceholder,
});

const SessionListLinkOverlays = dynamic(loadSessionListLinkOverlays, {
  ssr: false,
  loading: rethrowLoadError,
});

const ConsolidatedSessionLinkAvatar = dynamic(loadConsolidatedSessionLinkAvatar, {
  ssr: false,
  loading: PatientLinkAvatarPlaceholder,
});

const SessionsListFooterLinkControl = dynamic(loadSessionsListFooterLinkControl, {
  ssr: false,
  loading: rethrowLoadError,
});

// Holds the v1 row's 46px footprint so `VList` rows don't collapse to 0px while the chunk loads.
export const accordionNavV3ButtonFallback = ({ error }: { error?: Error | null }) => {
  if (error) throw error;
  return createElement('div', { className: 'h-[46px] w-full', 'aria-hidden': true });
};

const AccordionNavV3Button = dynamic(loadAccordionNavV3Button, {
  ssr: false,
  loading: accordionNavV3ButtonFallback,
});

const UploadSessionScheduleModal = dynamic(loadUploadSessionScheduleModal, {
  ssr: false,
});

const LazySessionlessEvidenceAwarenessPopover = dynamic(
  loadSessionlessEvidenceAwarenessPopover,
  {
    ssr: false,
  }
);

const SessionlessEvidenceAwarenessPopover = (props: { variant: 'sidebar' }) =>
  createElement(LazySessionlessEvidenceAwarenessPopover, props);

const EhrRePairBanner = dynamic(loadEhrRePairBanner, { ssr: false });

// Action-center slots. Static imports would put the modal and the export
// submenu in every route that mounts the sidebar, including haiku.
export const loadSendAsEmailModal = () =>
  import('@/components/ui/more-dropdown-menu/send-as-email').then((mod) => ({
    default: mod.default,
  }));

export const loadExportSubmenu = () =>
  import('@/composition/hosts/session/export-files/export-format-items/export-format-items').then(
    (mod) => ({
      default: mod.ExportSubmenu,
    })
  );

const SendAsEmailModal = dynamic(loadSendAsEmailModal, {
  ssr: false,
  loading: rethrowLoadError,
});

const ExportSubmenu = dynamic(loadExportSubmenu, {
  ssr: false,
  loading: rethrowLoadError,
});

const useShowSendAsEmailModal = () => useAtomValue(showSendAsEmailModal);

const useSetShowSendAsEmailModal = () => useSetAtom(showSendAsEmailModal);

const useExportFontRequest = () => useAtomValue(exportFontModalAtom);

const useSetExportFontRequest = () => useSetAtom(exportFontModalAtom);

type SessionListTrackedEvent = Parameters<SessionListHost['analytics']['track']>[0];

/**
 * Every name the list can send, mapped onto the app tracker. A union member
 * with no entry fails this `satisfies` check.
 */
const SESSION_LIST_TRACKED_EVENT = {
  schedule_upload_started: TrackingEventName.ScheduleUploadStarted,
  sessions_filter_applied: TrackingEventName.SessionsFilterApplied,
  sessions_scope_changed: TrackingEventName.SessionsScopeChanged,
  active_session_navigation: TrackingEventName.ActiveSessionNavigation,
  screen_viewed: TrackingEventName.ScreenViewed,
  sessions_list_paginated: TrackingEventName.SessionsListPaginated,
  sessions_sort_changed: TrackingEventName.SessionsSortChanged,
  button_clicked: TrackingEventName.ButtonClicked,
  assistant_panel_action: TrackingEventName.AssistantPanelAction,
  document_exported: TrackingEventName.DocumentExported,
} satisfies Record<SessionListTrackedEvent['event_name'], TrackingEventName>;

/** The CASL ability has been written for this user. */
export function useHasUpdatedAbility(): boolean {
  return useAtomValue(hasUpdatedAbilityAtom);
}

/** The copilot pre-chart request, read inside React. */
const useCopilotPrechartRequest = () => useAtomValue(copilotPrechartRequestAtom);

/** The setter for the batch the orchestrator drains. */
const useSetCopilotPrechartBatch = () => useSetAtom(copilotPrechartBatchAtom);

/** Which sessions query the legacy filter dropdown is driving. */
export const useSessionQueryVersion = () => useAtomValue(sessionQueryVersionAtom);

/** Pins the legacy dropdown onto the v1 query while it is open. */
export const useSetSessionQueryVersion = () => useSetAtom(sessionQueryVersionAtom);

/** Review-member picker. The atom stays app-side. */
export const useSetSelectedReviewMember = () => useSetAtom(selectedReviewMemberAtom);

/** Keyboard shortcuts are suppressed (a modal or field owns the keys). */
export const useIsShortcutsDisabled = () => useAtomValue(isShortcutsDisabledAtom);

/** Spotlight tour step, or null when the tour is closed. */
export const useSessionListSpotlightStep = () =>
  useAtomValue(sessionListSpotlightStepAtom);

/** Whether step 4's expand control is mounted. */
export const useSessionListExpandAnchorAvailable = () =>
  useAtomValue(sessionListExpandAnchorAvailableAtom);

/** Writes the expand-anchor flag from the header that mounts it. */
export const useSetSessionListExpandAnchorAvailable = () =>
  useSetAtom(sessionListExpandAnchorAvailableAtom);

/** Opens the delete-session modal. The atom stays app-side. */
export const useSetDeleteSessionData = () => useSetAtom(deleteSessionDataAtom);

/** Evidence sidebar open flag. The atom stays app-side. */
export const useSetEvidenceSidebarOpen = () => useSetAtom(evidenceSidebarOpenAtom);

export const useChronicleDeviceRecordingInfo = () =>
  useAtomValue(chronicleDeviceRecordingInfoAtom);

export const useChronicleIsPaused = () => useAtomValue(chronicleIsPausedAtom);

export const useChronicleLiveOffline = () => useAtomValue(chronicleLiveOfflineAtom);

export const useChronicleReconnectedPendingEnd = () =>
  useAtomValue(chronicleReconnectedPendingEndAtom);

export const useChronicleSessionId = () => useAtomValue(chronicleSessionIdAtom);

export const useBulkSyncPhase = () => useAtomValue(bulkSyncPhaseAtom);

export const useBulkSyncUploadProgress = () => useAtomValue(bulkSyncUploadProgressAtom);

export const useBulkSyncExpectedUploadTotal = () =>
  useAtomValue(bulkSyncExpectedUploadTotalAtom);

export const useBulkSyncCumulativeProcessed = () =>
  useAtomValue(bulkSyncCumulativeProcessedAtom);

export const useActiveSessionId = () => useAtomValue(activeSessionIdAtom);

export const useInFlightRecordingSessionId = () =>
  useAtomValue(inFlightRecordingSessionIdAtom);

export const useSetPendingRecordingAction = () => useSetAtom(pendingRecordingActionAtom);

export const useOfflineCaptureRunning = () => useAtomValue(offlineCaptureRunningAtom);

export const useOfflineLocalSessions = () => useAtomValue(offlineLocalSessionsAtom);

export const useOfflineSyncQueue = () => useAtomValue(offlineSyncQueueAtom);

export const useOfflineDismissedSyncedBadgeIds = () =>
  useAtomValue(offlineDismissedSyncedBadgeIdsAtom);

export const useOfflineNoteFailedDismissedSessionIds = () =>
  useAtomValue(offlineNoteFailedDismissedSessionIdsAtom);

export const useOfflineNoteFailedSessionIds = () =>
  useAtomValue(offlineNoteFailedSessionIdsAtom);

/** Typed patient id for one offline session. Empty string means none. */
export const useOfflinePatientLink = (sessionId: string) =>
  useAtomValue(offlinePatientLinkAtomFamily(sessionId));

const useCanCollapse = () => useAtomValue(canCollapseAtom);

const useSetIsMobileHeaderSheetOpen = () => useSetAtom(isMobileHeaderSheetOpenAtom);

const useSidebarOpenTab = () => useAtomValue(sidebarOpenTabAtom);

const useSetSidebarPanelContent = () => useSetAtom(sidebarPanelContentAtom);

const useHasIntegration = () => useAtomValue(hasIntegrationAtom);

const useIntegrationCallbacks = () => useAtomValue(integrationCallbacksAtom);

const useSessionlessEvidenceAwarenessDevVariantOverride = () =>
  useAtomValue(sessionlessEvidenceAwarenessDevVariantOverrideAtom);

const useSessionlessEvidenceAwarenessDevForceShow = () =>
  useAtomValue(sessionlessEvidenceAwarenessDevForceShowAtom);

const useSessionRecordingStatus = () => useSessionRecordingStore((s) => s.status);

const trackSessionReviewPaywallViewed = (targetPlan: SubscriptionTier) =>
  trackPaywallViewed({ source: PaywallSource.SessionReview, target_plan: targetPlan });

const useSelectedReviewMember = () => useAtomValue(selectedReviewMemberAtom);

const useScheduleDate = () => useAtomValue(scheduleDateAtom);

const useScheduleDateState = () => useAtom(scheduleDateAtom);

const useSetSessionListAnchorsMounted = () => useSetAtom(sessionListAnchorsMountedAtom);

const startDurationVital = (name: string) => datadogRum.startDurationVital(name);

/**
 * One object, created once. Callers pass this reference; it is not rebuilt
 * per render, so it stays out of the first-load graph.
 */
export const sessionListHost: SessionListHost = {
  identity: {
    ability,
    ACTIONS,
    PERMISSION_SUBJECTS,
    useRBACPermissionCheck,
    useShowClinicianSelector,
    useTeamClinicianList,
    useQueryUserTier,
    useUserTier,
    useAuth,
    useNewSessionDelegation,
    useSetSelectedReviewMember,
    useHasUpdatedAbility,
    useCanViewOthersSessions,
    useIsDirectSessionSharingAllowed,
    useCombinedOrganizationInfo,
    useShowSessionReview,
    usePaywallStates,
    useSelectedReviewMember,
  },
  integrations: {
    useHasActiveEhrIntegration,
    useIntegrationsMarketplaceVisibilityGate,
    useIsEhrLaunch,
    useEhrLaunchSessionId,
    useHasIntegration,
    useIntegrationCallbacks,
    IntegrationLaunchSource,
    useIntegrationEhrInfo,
    useQueryIntegrationsMixinGetSchedule,
    useQueryEHRAppointments,
    useQueryUserEHR,
    useMutateStatePostIntegrationWorkflowSession,
    useMutateUpdateSessionEHRAppointmentId,
    TIME_FORMAT,
    getEhrApptTime,
    EhrRePairBanner,
    EhrIcon,
  },
  shell: {
    isOnPrem,
    useIsNewSessionListEnabled,
    useIsSessionUrgencyEnabled,
    IS_SIDEBAR_PANEL_ENABLED,
    IS_CONSOLIDATED_SESSIONS_LIST_ENABLED,
    getSidebarPanelOverlayTriggerProps,
    Loader,
    MobileDrawerHeader,
    MobileFilterRow,
    parseTimeStamp,
    useSettingsModal,
    useRouter,
    reportError: datadogRum.addError,
    useIsSessionPageV2Enabled,
    IS_LIVE_TRANSCRIPTION_INDICATOR_ENABLED,
    hotkeysList,
    useIsShortcutsDisabled,
    TrackedButton,
    SessionMergeIcon,
    useMediaQuery,
    useSessionListSpotlightStep,
    useSessionListExpandAnchorAvailable,
    useSetSessionListExpandAnchorAvailable,
    useSessionListSpotlightActions,
    SidebarTab,
    useSidebarOpenTab,
    setSidebarOpenTab,
    useSetSidebarPanelContent,
    useVisibleSidebarPanelContent,
    useCanCollapse,
    useSetIsMobileHeaderSheetOpen,
    DatadogDurationVital,
    DatadogDurationVitalStopper,
    GET_EVIDENCE_AWARENESS_POPUP_VARIANT,
    IS_ASSISTANT_VIEW_ENABLED,
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
    DEFAULT_DATE_FORMAT,
    useSessionsListHotkeys,
    useTutorial,
    ProfileSearchSvg,
    HeidiTogetherBoxesSvg,
    AccordionNavV3Button,
    UploadSessionScheduleModal,
    useShowSessionsListAssistantView,
    IS_UPCOMING_SESSIONS_ENABLED,
    useIsCalendarEventTitleEnabled,
    useIsMultiProviderScheduleEnabled,
    useIsProblemBasedChartingEnabled,
    useIsScheduleFilterOptionsEnabled,
    useSetSessionListAnchorsMounted,
    statusIconColors,
    startDurationVital,
    FILENAME_SAFE_DATETIME_FORMAT,
    proseMirrorClassName: styles.ProseMirror ?? '',
    SendAsEmailModal,
    useShowSendAsEmailModal,
    useSetShowSendAsEmailModal,
    useCurrentTime,
    simpleMarkdownToHtml,
    printDocument,
    useBlockEditorMarkdown,
    ExportSubmenu,
    useExportFontRequest,
    useSetExportFontRequest,
    IS_HIDE_EMAIL_FUNCTIONALITY_ENABLED,
  },
  sessionApp: {
    useSessionDateDisplay,
    useSessionListVersion,
    useSessionQueryVersion,
    useSetSessionQueryVersion,
    useVisibleSessionStatuses,
    sessionStatusChipConfig,
    statusIconVariants,
    statusChipVariants,
    SESSION_STATUS_DISPLAY_ORDER,
    sessionUrgencyMessages,
    sessionStatusDescription: enumToDescription,
    useNewSession,
    useNewSessionMutationPending,
    useDebouncedNewSession,
    useQueryCombinedSessions,
    usePrefetchSessionById,
    useMutateUpdateSessionStatus,
    useMutateUpdateSyncDocumentById,
    useSessionCopy,
    useMutateDeleteEmptySessions,
    useFormatSessionTime,
    filterAndSortSessions,
    buildSessionStatusOptions,
    isSessionUrgent,
    SessionUrgencyFlag,
    useSetDeleteSessionData,
    compareSessionsByStatus,
    useSessionRecordingStatus,
    useScheduleDate,
    useScheduleDateState,
    useMutateCreateNewSession,
    useGetPageSize,
  },
  analytics: {
    track: (event) => {
      track({
        ...event,
        event_name: SESSION_LIST_TRACKED_EVENT[event.event_name],
      } as TrackEvent);
    },
    TrackingEventName: {
      ScheduleUploadStarted: TrackingEventName.ScheduleUploadStarted,
      SessionsFilterApplied: TrackingEventName.SessionsFilterApplied,
      SessionsScopeChanged: TrackingEventName.SessionsScopeChanged,
      ActiveSessionNavigation: TrackingEventName.ActiveSessionNavigation,
      ScreenViewed: TrackingEventName.ScreenViewed,
      SessionsListPaginated: TrackingEventName.SessionsListPaginated,
      SessionsSortChanged: TrackingEventName.SessionsSortChanged,
      ButtonClicked: TrackingEventName.ButtonClicked,
      AssistantPanelAction: TrackingEventName.AssistantPanelAction,
      DocumentExported: TrackingEventName.DocumentExported,
    },
    trackButtonClicked,
    // ShortcutContext is a nominal enum. The names we forward are its members.
    trackShortcutUsed:
      trackShortcutUsed as SessionListHost['analytics']['trackShortcutUsed'],
    ShortcutContext,
    useSessionSearchTracking,
    useSessionsPanelTracking,
    trackScheduleUploadCompleted,
    trackScheduleUploadProcessingFailed,
    trackScheduleUploadSessionsCreated,
    trackScheduleSynced,
    trackScheduleViewed,
    trackScheduleUploadPromoViewed,
    trackScheduleUploadPromoClicked,
  },
  foreignApp: {
    useIsAmbientCopilotEnabled,
    logCopilotAction,
    COPILOT_PRECHART_REQUEST_TTL_MS,
    useCopilotPrechartRequest,
    useSetCopilotPrechartBatch,
    getCopilotPrechartRequest: () => getDefaultStore().get(copilotPrechartRequestAtom),
    setCopilotPrechartRequest: (request) => {
      getDefaultStore().set(copilotPrechartRequestAtom, request);
    },
    useSetEvidenceSidebarOpen,
    useSessionlessEvidenceAwarenessDevVariantOverride,
    useSessionlessEvidenceAwarenessDevForceShow,
    SessionlessEvidenceAwarenessPopover,
    trackSessionReviewPaywallViewed,
  },
  desktop: {
    useOfflineGating,
    OfflineGatedTooltip,
    useIsOfflineRecordingEnabled,
    useIsDesktopChronicleEnabled,
    useIsReleaseRemoteSyncAsyncWebEnabled,
    useIsInterruptedRemoteSession,
    useIsLiveOfflineRemoteSession,
    useIsLiveRunningRemoteSession,
    useIsPureOfflineRemoteSession,
    useIsRemoteReconnectedSession,
    useQueryInterruptedSessions,
    useTidyUpRecordingLock,
    useChronicleDeviceRecordingInfo,
    useChronicleIsPaused,
    useChronicleLiveOffline,
    useChronicleReconnectedPendingEnd,
    useChronicleSessionId,
    useBulkSyncPhase,
    useBulkSyncUploadProgress,
    useBulkSyncExpectedUploadTotal,
    useBulkSyncCumulativeProcessed,
    useActiveSessionId,
    useInFlightRecordingSessionId,
    useSetPendingRecordingAction,
    useOfflineCaptureRunning,
    useOfflineLocalSessions,
    useOfflineSyncQueue,
    useOfflinePendingServerDeleteIds,
    useOfflineDismissedSyncedBadgeIds,
    useOfflineNoteFailedDismissedSessionIds,
    useOfflineNoteFailedSessionIds,
    useOfflinePatientLink,
    offlineGatingMessages,
    SyncStatusBadge,
    useOfflineAwareSyncStatus,
    RemoteDeviceSvg,
    isOfflineDeletableStatus,
  },
  patients: {
    useQueryPatientProfiles,
    PatientLinkAvatar,
    SessionListLinkOverlays,
    ConsolidatedSessionLinkAvatar,
    SessionsListFooterLinkControl,
    useMutateCreateAndLinkPatientProfile,
    useMutateLinkSessionsToPatientProfile,
    trackPatientSelected,
  },
};
